/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { LatLngLiteral } from 'leaflet';
import {
  DroneCommands,
  LivePageWidgetType,
  VideoStreamOption,
  PiPModeOption,
} from '../utils';
import {
  HOTKEY_REPEAT_INTERVAL,
  LIVE_PAGE_WIDGET_SIZES,
} from '../utils/constants';

export interface IDroneOrbitPoint extends LatLngLiteral {
  alt: number;
  radius: number;
}

interface LivePageState {
  isLeftNavExpanded: boolean;
  isTopRightWidgetVisible: boolean;
  isBottomHUDVisible: boolean;
  ishotkeyKeybindsOpen: boolean;
  isHotkeysEnabled: boolean;
  hotkeyRepeatInterval: number;
  topRightWidgetType: LivePageWidgetType;
  topRightWidgetSize: {
    width: number;
    height: number;
  };
  isMouseGimbalControlEnabled: boolean;
  shouldAnimateToCenter: boolean;
  videoStreamOption: VideoStreamOption;
  showWaypointHeadings: boolean;
  isDroneCommandConfirmationModalOpen: boolean;
  currentDroneCommand: DroneCommands | null;
  isFlyToModalOpen: boolean;
  droneOrbitPoint: IDroneOrbitPoint | null;
  droneFlyToPoint: {
    coordinates: LatLngLiteral | null;
    isDFR: boolean;
  };
  droneGazePoint: LatLngLiteral | null;
  droneCommandPoint: LatLngLiteral | null;
  dfrEventFlyingTo: any | null;
  isNotesWidgetOpen: boolean;
  currentRGBTimestamp: number | unknown;
  currentThermalTimestamp: number | unknown;
  socketDroneOrbitPoint: IDroneOrbitPoint | null;
  showGamePadControls: boolean;
  notesTimeStamp: number;
  currentRGBzoom: number;
  currentThermalZoom: number;
  futureZoomLevel: number;
  futureThermalZoomLevel: number;
  currentPiPMode: PiPModeOption;
  livePageSelectedDroneName: any;
  enableFullScreen: boolean;
  enableFullScreenThermal: boolean;
  geoFenceCoordinates: any;
  hotkeyLogSize: { width: number; height: number };
  hotkeyLog: { isError: boolean; message: string }[];
  isGoingForward: boolean;
  isGoingBackward: boolean;
  isGoingLeft: boolean;
  isGoingRight: boolean;
  isGoingUp: boolean;
  isGoingDown: boolean;
  isCameraGoingLeft: boolean;
  isCameraGoingRight: boolean;
  isCameraGoingUp: boolean;
  isCameraGoingDown: boolean;
  isCompassVisible?: boolean;
  compassFov: number;
}

const initialState: LivePageState = {
  isLeftNavExpanded: true,
  isTopRightWidgetVisible: true,
  isBottomHUDVisible: true,
  ishotkeyKeybindsOpen: false,
  isHotkeysEnabled: true,
  hotkeyRepeatInterval: HOTKEY_REPEAT_INTERVAL,
  isMouseGimbalControlEnabled: false,
  shouldAnimateToCenter: false,
  topRightWidgetType: LivePageWidgetType.VIDEO,
  topRightWidgetSize: {
    width: LIVE_PAGE_WIDGET_SIZES?.default?.width,
    height: LIVE_PAGE_WIDGET_SIZES?.default?.height,
  },
  videoStreamOption: VideoStreamOption.RGB,
  showWaypointHeadings: false,
  isDroneCommandConfirmationModalOpen: false,
  currentDroneCommand: null,
  isFlyToModalOpen: false,
  droneOrbitPoint: null,
  droneFlyToPoint: {
    coordinates: null,
    isDFR: false,
  },
  droneGazePoint: null,
  droneCommandPoint: null,
  dfrEventFlyingTo: null,
  isNotesWidgetOpen: false,
  currentRGBTimestamp: null,
  currentThermalTimestamp: null,
  socketDroneOrbitPoint: null,
  showGamePadControls: false,
  notesTimeStamp: 0,
  currentRGBzoom: 0,
  currentThermalZoom: 0,
  futureZoomLevel: 1,
  futureThermalZoomLevel: 1,
  currentPiPMode: PiPModeOption.NONE,
  livePageSelectedDroneName: '',
  enableFullScreen: false,
  enableFullScreenThermal: false,
  geoFenceCoordinates: null,
  hotkeyLogSize: { width: 450, height: 200 },
  hotkeyLog: [],
  isGoingForward: false,
  isGoingBackward: false,
  isGoingLeft: false,
  isGoingRight: false,
  isGoingUp: false,
  isGoingDown: false,
  isCameraGoingLeft: false,
  isCameraGoingRight: false,
  isCameraGoingUp: false,
  isCameraGoingDown: false,
  isCompassVisible: false,
  compassFov: 120,
};

export const livePageSlice = createSlice({
  name: 'livePage',
  initialState,
  reducers: {
    toggleIsLeftNavExpanded: (state, action: PayloadAction<boolean>) => {
      state.isLeftNavExpanded = action.payload;
    },
    toggleIsTopRightWidgetVisible: (state, action: PayloadAction<boolean>) => {
      state.isTopRightWidgetVisible = action.payload;
    },
    toggleIsBottomHUDVisible: (state, action: PayloadAction<boolean>) => {
      state.isBottomHUDVisible = action.payload;
    },
    toggleIsHotkeyKeybindsOpen: (state, action: PayloadAction<boolean>) => {
      state.ishotkeyKeybindsOpen = action.payload;
    },
    setIsHotkeysEnabled: (state, action: PayloadAction<boolean>) => {
      state.isHotkeysEnabled = action.payload;
    },
    setHotkeyRepeatInterval: (state, action: PayloadAction<number>) => {
      state.hotkeyRepeatInterval = action.payload;
    },
    setIsMouseGimbalControlEnabled: (state, action: PayloadAction<boolean>) => {
      state.isMouseGimbalControlEnabled = action.payload;
    },
    setShouldAnimateToCenter: (state, action: PayloadAction<boolean>) => {
      state.shouldAnimateToCenter = action.payload;
    },
    setWidgetType: (state, action: PayloadAction<LivePageWidgetType>) => {
      state.topRightWidgetType = action.payload;
    },
    setTopRightWidgetSize: (
      state,
      action: PayloadAction<{ width: number; height: number }>
    ) => {
      state.topRightWidgetSize = action.payload;
    },
    setVideoStreamOption: (state, action: PayloadAction<VideoStreamOption>) => {
      state.videoStreamOption = action.payload;
    },
    toggleShowWaypointHeadings: (state, action: PayloadAction<boolean>) => {
      state.showWaypointHeadings = action.payload;
    },
    toggleIsDroneCommandConfirmationModalOpen: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isDroneCommandConfirmationModalOpen = action.payload;
    },
    setCurrentDroneCommand: (
      state,
      action: PayloadAction<DroneCommands | null>
    ) => {
      state.currentDroneCommand = action.payload;
    },
    setDroneCommandPoint: (
      state,
      action: PayloadAction<LatLngLiteral | null>
    ) => {
      state.droneCommandPoint = action.payload;
    },
    setDfrEventFlyingTo: (state, action: PayloadAction<any | null>) => {
      state.dfrEventFlyingTo = action.payload;
    },
    toggleIsFlyToModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isFlyToModalOpen = action.payload;
    },
    setDroneOrbitPoint: (
      state,
      action: PayloadAction<IDroneOrbitPoint | null>
    ) => {
      state.droneOrbitPoint = action.payload;
    },
    setDroneFlyToPoint: (
      state,
      action: PayloadAction<{
        coordinates: LatLngLiteral | null;
        isDFR: boolean;
      }>
    ) => {
      state.droneFlyToPoint = action.payload;
    },
    setDroneGazePoint: (state, action: PayloadAction<LatLngLiteral | null>) => {
      state.droneGazePoint = action.payload;
    },
    toggleIsNotesWidgetOpen: (state, action: PayloadAction<boolean>) => {
      state.isNotesWidgetOpen = action.payload;
    },
    setCurrentRGBTimestamp: (
      state,
      action: PayloadAction<number | unknown>
    ) => {
      state.currentRGBTimestamp = action.payload;
    },
    setCurrentThermalTimestamp: (
      state,
      action: PayloadAction<number | unknown>
    ) => {
      state.currentThermalTimestamp = action.payload;
    },
    setSocketDroneOrbitPoint: (
      state,
      action: PayloadAction<IDroneOrbitPoint | null>
    ) => {
      state.socketDroneOrbitPoint = action.payload;
    },
    toggleShowGamePadControls: (state, action: PayloadAction<boolean>) => {
      state.showGamePadControls = action.payload;
    },
    setNotesTimeStamp: (state, action: PayloadAction<number | 0>) => {
      state.notesTimeStamp = action.payload;
    },
    setCurrentRGBZoom: (state, action: PayloadAction<number>) => {
      state.currentRGBzoom = action.payload;
    },
    setCurrentThermalZoom: (state, action: PayloadAction<number>) => {
      state.currentThermalZoom = action.payload;
    },
    setFutureZoomLevel: (state, action: PayloadAction<number | 1>) => {
      state.futureZoomLevel = action.payload;
    },
    setFutureThermalZoomLevel: (state, action: PayloadAction<number | 1>) => {
      state.futureThermalZoomLevel = action.payload;
    },
    setCurrentPiPMode: (state, action: PayloadAction<PiPModeOption>) => {
      state.currentPiPMode = action.payload;
    },
    setLivePageSelectedDroneName: (state, action: PayloadAction<string>) => {
      state.livePageSelectedDroneName = action.payload;
    },
    setEnableFullScreen: (state, action: PayloadAction<boolean>) => {
      state.enableFullScreen = action.payload;
    },
    setEnableFullScreenThermal: (state, action: PayloadAction<boolean>) => {
      state.enableFullScreenThermal = action.payload;
    },
    setGeoFenceCoordinates: (state, action: PayloadAction<any>) => {
      state.geoFenceCoordinates = action.payload;
    },
    setHotkeyLogSize: (
      state,
      action: PayloadAction<{ width: number; height: number }>
    ) => {
      state.hotkeyLogSize = action.payload;
    },
    setHotkeyLog: (
      state,
      action: PayloadAction<{ isError: boolean; message: string }>
    ) => {
      state.hotkeyLog.push(action.payload);
    },
    clearHotkeyLog: (state) => {
      state.hotkeyLog = [];
    },
    setIsGoingForward: (state, action: PayloadAction<boolean>) => {
      state.isGoingForward = action.payload;
    },
    setIsGoingBackward: (state, action: PayloadAction<boolean>) => {
      state.isGoingBackward = action.payload;
    },
    setIsGoingLeft: (state, action: PayloadAction<boolean>) => {
      state.isGoingLeft = action.payload;
    },
    setIsGoingRight: (state, action: PayloadAction<boolean>) => {
      state.isGoingRight = action.payload;
    },
    setIsGoingUp: (state, action: PayloadAction<boolean>) => {
      state.isGoingUp = action.payload;
    },
    setIsGoingDown: (state, action: PayloadAction<boolean>) => {
      state.isGoingDown = action.payload;
    },
    setIsCameraGoingLeft: (state, action: PayloadAction<boolean>) => {
      state.isCameraGoingLeft = action.payload;
    },
    setIsCameraGoingRight: (state, action: PayloadAction<boolean>) => {
      state.isCameraGoingRight = action.payload;
    },
    setIsCameraGoingUp: (state, action: PayloadAction<boolean>) => {
      state.isCameraGoingUp = action.payload;
    },
    setIsCameraGoingDown: (state, action: PayloadAction<boolean>) => {
      state.isCameraGoingDown = action.payload;
    },
    setIsCompassVisible: (state, action: PayloadAction<boolean>) => {
      state.isCompassVisible = action.payload;
    },
    setCompassFov: (state, action: PayloadAction<number>) => {
      state.compassFov = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  toggleIsLeftNavExpanded,
  toggleIsTopRightWidgetVisible,
  toggleIsBottomHUDVisible,
  toggleIsHotkeyKeybindsOpen,
  setIsHotkeysEnabled,
  setHotkeyRepeatInterval,
  setIsMouseGimbalControlEnabled,
  setShouldAnimateToCenter,
  setWidgetType,
  setTopRightWidgetSize,
  setVideoStreamOption,
  toggleShowWaypointHeadings,
  toggleIsDroneCommandConfirmationModalOpen,
  setCurrentDroneCommand,
  toggleIsFlyToModalOpen,
  setDroneOrbitPoint,
  setDroneFlyToPoint,
  setDroneGazePoint,
  setDroneCommandPoint,
  setDfrEventFlyingTo,
  toggleIsNotesWidgetOpen,
  setCurrentRGBTimestamp,
  setCurrentThermalTimestamp,
  setSocketDroneOrbitPoint,
  toggleShowGamePadControls,
  setNotesTimeStamp,
  setCurrentRGBZoom,
  setCurrentThermalZoom,
  setFutureZoomLevel,
  setFutureThermalZoomLevel,
  setCurrentPiPMode,
  setLivePageSelectedDroneName,
  setEnableFullScreen,
  setEnableFullScreenThermal,
  setGeoFenceCoordinates,
  setHotkeyLogSize,
  setHotkeyLog,
  clearHotkeyLog,
  setIsGoingForward,
  setIsGoingBackward,
  setIsGoingLeft,
  setIsGoingRight,
  setIsGoingUp,
  setIsGoingDown,
  setIsCameraGoingLeft,
  setIsCameraGoingRight,
  setIsCameraGoingUp,
  setIsCameraGoingDown,
  setIsCompassVisible,
  setCompassFov,
} = livePageSlice.actions;

export default livePageSlice.reducer;
